import { capitalize, computed } from 'vue';
import { IVariant } from '../foundation/interfaces/IVariant';

/**
 * Helpful functions that return computed car data.
 *
 * Note: The car data is based off the getVehicleById API endpoint.
 *
 * @param carData
 *
 * @returns
 */
export function useCarData(carData: Record<string, any>) {
  /**
   *
   */
  const makeModel = computed(() => `${carData.Make} ${carData.Model}`);

  /**
   * Show car body, doors, wheels, seats data (if present)
   *
   * PJ306 - 753 Error and Exception Handling
   * FR1.1 - When information is missing, then the data point should not be shown.
   *
   **/
  const bodyDoorsSeats = computed(() => {
    let result = capitalize(carData.Body_type || '');

    if (carData.Body_type && carData.Doors) {
      result += `, ${carData.Doors} doors`;
    } else if (carData.Doors) {
      result += `${carData.Doors} doors`;
    }

    if ((carData.Body_type || carData.Doors) && carData.Seats) {
      result += `, ${carData.Seats} seats`;
    } else if (carData.Seats) {
      result += `${carData.Seats} seats`;
    }

    return result;
  });

  /**
   * Show the fuel type and range based on vehicle type (EV, ICE, Hybrid, etc).
   *
   * Assumptions / rules (as of 06/04/23) are as follows:
   *
   * - Plug in hybrid:   Show primary fuel type and range as well as secondary fuel type and range.
   * - ICE:              Show fuel type and range lt/100km
   * - Hybrid:           Show fuel type and range lt/100km
   */
  const fuelTypeAndRange = computed(() => {
    const powertrain = (carData.Powertrain || carData.Overall_Powertrain)?.toLowerCase();
    let range = '';

    switch (powertrain) {
      case 'hybrid electric vehicle':
      case 'hybrid electric vehicle (plug-in)':
      case 'plug-in hybrid electric vehicle': {
        // Build up an array with the fuel types and values if they exist
        let rangeArrayStr: string[] = [];

        if (carData?.Primary_engine_motor_Fuel_type) {
          rangeArrayStr.push(carData.Primary_engine_motor_Fuel_type);
        }

        const rangeValue = Number(carData.Driving_range_Electric_motor_only_km);
        if (rangeValue) {
          rangeArrayStr.push(`${rangeValue}km range`);
        }

        if (carData?.Secondary_engine_motor_Fuel_type && carData?.Secondary_engine_motor_Fuel_type?.toLowerCase() !== 'none') {
          rangeArrayStr.push(capitalize(carData.Secondary_engine_motor_Fuel_type));
        }

        if (carData.Fuel_consumption_Combined_ADR8102_lt_100km && Number(carData.Fuel_consumption_Combined_ADR8102_lt_100km) > 0) {
          rangeArrayStr.push(carData.Fuel_consumption_Combined_ADR8102_lt_100km + 'L/100km');
        }

        if (rangeArrayStr.length) {
          range = rangeArrayStr.join(', ');
        }

        break;
      }

      case 'bev':
      case 'electric':
      case 'electric vehicle':
      case 'battery electric vehicle':
      case 'fuel-cell electric vehicle':
      case 'extended-range electric vehicle': {
        const rangeValue = Number(carData.Driving_range_Electric_motor_only_km) || carData.Driving_range_ADR8102_combined_km;
        const rangeValueString = rangeValue ? `, ${rangeValue}km range` : '';

        range = carData.Primary_engine_motor_Fuel_type + rangeValueString;
        break;
      }

      case 'combustion':
      case 'mild hybrid electric vehicle':
      case 'mhev': {
        // Build up an array with the fuel values if they exist
        let rangeArrayStr: string[] = [];
        if (carData.Primary_engine_motor_Fuel_type?.length) {
          rangeArrayStr.push(carData.Primary_engine_motor_Fuel_type);
        }
        if (carData.Fuel_consumption_Combined_ADR8102_lt_100km && Number(carData.Fuel_consumption_Combined_ADR8102_lt_100km) > 0) {
          rangeArrayStr.push(carData.Fuel_consumption_Combined_ADR8102_lt_100km + 'L/100km');
        }

        if (rangeArrayStr.length) {
          range = rangeArrayStr.join(', ');
        }
        break;
      }

      default:
        range = carData.Primary_engine_motor_Fuel_type || carData.Fuel_type || 'No data available';
    }

    if (!range.length) {
      range = carData.Primary_engine_motor_Fuel_type || carData.Fuel_type || 'No data available';
    }

    return capitalize(range);
  });

  /**
   * Show the emissions rating based on car environment score.
   */
  const emissionsRating = computed(() => {
    const envScoreNumber = Number(carData.environment_score || carData.Environmentscore);
    let emissions = '';

    switch (envScoreNumber) {
      case 1:
      case 2: {
        emissions = 'Very high';
        break;
      }

      case 3:
      case 4: {
        emissions = 'High';
        break;
      }

      case 5:
      case 6: {
        emissions = 'Average';
        break;
      }

      case 7:
      case 9:
      case 8: {
        emissions = 'Low';
        break;
      }
      
      case 10: {
        emissions = 'Zero';
        break;
      }

      default:
        return 'Data not available';
    }

    return emissions + ' emissions';
  });

  const formattedCarPrice = computed(() =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AUD',
      currencyDisplay: 'narrowSymbol',
      currencySign: 'accounting',
      maximumFractionDigits: 0,
    }).format(Number(carData.RACQ_PRICE))
  );

  function getVariantYearRange(currentVariant: IVariant): string {
    let range = '';

    if (currentVariant?.yearLow && currentVariant?.yearHigh) {
      if (currentVariant.yearLow === currentVariant.yearHigh) {
        return String(currentVariant.yearLow);
      }

      return `${currentVariant.yearLow} - ${currentVariant.yearHigh}`;
    }

    if (currentVariant?.yearLow) {
      range += currentVariant.yearLow;
    } else if (currentVariant?.yearHigh) {
      range += currentVariant.yearHigh;
    } else {
      range = 'No year data';
    }

    return range;
  }

  return {
    makeModel,
    bodyDoorsSeats,
    formattedCarPrice,
    fuelTypeAndRange,
    emissionsRating,
    getVariantYearRange,
  };
}
